// React
import { ReactNode, useCallback } from 'react'

// Interfaces
import { IQuestionHookProps } from './interfaces'
import { ISharedQuestionGroup, ISharedQuestionSetResultDetail } from 'interfaces/shared'
import { ISharedCommonFileUploadAttrs, ISharedCommonUpdateAnswerAttrs } from 'interfaces/shared/shared-common.interface'

// Antd
import { Checkbox, Radio, Space, SpaceProps } from 'antd'
import { SliderMarks } from 'antd/lib/slider'

// Constant
import { QUESTION_TYPES } from 'constant/shared.constant'

// Components
import {
  BaseLabel,
  BaseRawHtml,
  BaseSlider,
  BaseUploaderInlineMultiple,
  FormDatePicker,
  FormTextArea,
  FormTextField,
} from 'components'

// Moment
import moment from 'moment'

// Lodash
import isEmpty from 'lodash/isEmpty'
import flattenDeep from 'lodash/flattenDeep'
import debounce from 'lodash/debounce'

// Question Types
const { TEXT, MULTIPLE_CHOICE, IMAGE, PARAGRAPH, SHORT_ANSWER, DATE, TIME, CHECKBOX, SLIDER, FILE_UPLOAD } = QUESTION_TYPES

// Slider Marks
const SLIDER_MARKS: SliderMarks = {
  1: '1',
  2: '2',
  3: '3',
  4: '4',
  5: '5',
}

const useQuestion = ({ isReadOnly }: IQuestionHookProps) => {
  /**
   * @description Handle answer callback
   *
   * @param {function} callback
   * @param {ISharedQuestionSetResultDetail} record
   * @param {any} data
   *
   * @return {void} void
   */
  const answerCallback = useCallback(
    (
      callback: (identifiers: ISharedCommonUpdateAnswerAttrs, data: any, attrs?: ISharedCommonFileUploadAttrs) => void,
      record: ISharedQuestionSetResultDetail,
      data: any,
      attrs?: ISharedCommonFileUploadAttrs,
    ): void => {
      // Note: Every '' or 0 value in this callback, that should be the default value, you must assign in the parent / component
      callback(
        {
          resultId: '',
          resultDetailId: record.id!,
          currentValue: record,
          questionSetId: 0,
          value: data,
          fileId: record.fileId!,
        },
        data,
        attrs,
      )
    },
    [],
  )

  /**
   * @description Handle answer callback, but debounce
   *
   * @param {function} callback
   * @param {ISharedQuestionSetResultDetail} record
   * @param {any} data
   *
   * @return {void} void
   */
  const answerCallbackDebounced = debounce(
    (
      callback: (identifiers: ISharedCommonUpdateAnswerAttrs, data: any, attrs?: ISharedCommonFileUploadAttrs) => void,
      record: ISharedQuestionSetResultDetail,
      data: any,
    ) => {
      answerCallback(callback, record, data)
    },
    1000,
  )

  /**
   * @description Render question
   *
   * @param {ISharedQuestionSetResultDetail} record
   *
   * @return {ReactNode} ReactNode
   */
  const question_renderQuestion = useCallback((record: ISharedQuestionSetResultDetail): ReactNode => {
    switch (record.question?.questionType) {
      case TEXT:
        return <BaseRawHtml html={record.question?.question!} />
      case IMAGE:
        return <img src={record.question?.questionFileUrl!} alt={record.question?.question!} />
      default:
        return <></>
    }
  }, [])

  /**
   * @description Render answer
   *
   * @param {ISharedQuestionSetResultDetail} record
   * @param {function} callback
   *
   * @return {ReactNode} ReactNode
   */
  const question_renderAnswer = useCallback(
    (
      record: ISharedQuestionSetResultDetail,
      callback: (identifiers: ISharedCommonUpdateAnswerAttrs, data: any) => void,
    ): ReactNode => {
      if (record.answer !== null) {
        const { answerLayout, answerType, answerValue } = record.answer
        const _answerLayout: any = answerLayout

        // Check if answer readonly
        if (answerType !== FILE_UPLOAD && isReadOnly) {
          // Check if user answer is using Multiple Choice.
          if (answerType === MULTIPLE_CHOICE) {
            return record.answer.answerOption.find(answerOption => answerOption.isSelected)?.label || ''
          }

          // Check if user answer is using paragraph, short text, date, or time.
          if ([PARAGRAPH, SHORT_ANSWER, DATE, TIME].includes(answerType!)) {
            return answerValue
          }

          return 'Todo: Answer'
        }

        // Check if answer type is multiple choice
        if (answerType === MULTIPLE_CHOICE) {
          const answer = record.answer.answerOption.find(answerOption => answerOption.isSelected)

          return (
            <div className="flex flex-col gap-2">
              <Radio.Group value={answer?.value} onChange={(event: any) => answerCallback(callback, record, event.target.value)}>
                <Space direction={_answerLayout.toLowerCase() as SpaceProps['direction']} align="start">
                  {record.answer.answerOption.length > 0 &&
                    record.answer?.answerOption?.map(option => (
                      <Radio value={option.value} key={option.value}>
                        {option.label}
                      </Radio>
                    ))}
                </Space>
              </Radio.Group>
            </div>
          )
        }

        // Check if answer type is checkbox
        if (answerType === CHECKBOX) {
          return (
            <Checkbox.Group
              options={
                record.answer.answerOption.map(({ label, value }) => ({ label, value })) as { label: string; value: any | null }[]
              }
              defaultValue={
                record.answer.answerOption
                  .filter(answerOption => answerOption.isSelected)
                  .map(answerOption => answerOption.value) as string[]
              }
              style={{ display: 'flex', flexDirection: 'column' }}
              onChange={event => answerCallback(callback, record, event)}
            />
          )
        }

        // Check if answer type is paragraph
        if (answerType === PARAGRAPH) {
          return (
            <FormTextArea
              showCount
              maxLength={8000}
              defaultValue={answerValue || ''}
              onChange={event => answerCallbackDebounced(callback, record, event.target.value)}
              style={{
                height: 125
              }}
            />
          )
        }

        // Check if answer type is short answer
        if (answerType === SHORT_ANSWER) {
          return (
            <FormTextField
              defaultValue={answerValue || ''}
              onChange={event => answerCallback(callback, record, event.target.value)}
            />
          )
        }

        // Check if answer type is date picker
        if (answerType === DATE) {
          return (
            <FormDatePicker
              defaultValue={moment(answerValue)?.toString() === 'Invalid date' ? undefined : moment(answerValue)}
              onChange={event => answerCallback(callback, record, moment(event).format('YYYY-MM-DD')?.toString())}
            />
          )
        }

        // Check if answer type is slider
        if (answerType === SLIDER) {
          return (
            <div className="flex flex-col w-full">
              <div className="flex flex-col lg:flex-row items-center justify-between">
                <BaseLabel label={'Sangat Tidak Puas'} className="font-12 text-grey font-weight-400" />
                <BaseLabel label={'Sangat Puas'} className="font-12 text-grey font-weight-400" />
              </div>
              <BaseSlider marks={SLIDER_MARKS} min={1} max={5} onChange={event => answerCallback(callback, record, event)} />
            </div>
          )
        }

        // Check If answer type is files
        if (answerType === FILE_UPLOAD) {
          return (
            <BaseUploaderInlineMultiple
              files={record.answer.answerFiles}
              isReadOnly={isReadOnly}
              onAddItem={() => answerCallback(callback, record, null, { isAddFileItem: true })}
              onUpload={(identifiers, value) =>
                answerCallback(callback, { ...record, ...identifiers }, value, { isUpload: true })
              }
              onDelete={identifiers => answerCallback(callback, { ...record, ...identifiers }, null, { isDelete: true })}
              onDeleteItem={identifiers =>
                answerCallback(callback, { ...record, ...identifiers }, null, { isDeleteFileItem: true })
              }
              onDownload={identifiers => answerCallback(callback, { ...record, ...identifiers }, null, { isDownload: true })}
            />
          )
        }
      }

      return <></>
    },
    [isReadOnly, answerCallback, answerCallbackDebounced],
  )

  /**
   * @description Validate
   *
   * @param {ISharedQuestionSet[]} questionSets
   * @param {object} attrs
   *
   * @return {any} any
   */
  const question_validate = useCallback((questionGroup: ISharedQuestionGroup[], attrs?: { isForAll?: boolean }) => {
    const map = questionGroup.map(questionGroup => {
      return questionGroup.result.map(result => {
        return result.resultDetail
          .map(resultDetail => {
            return { ...resultDetail.answer }
          })
          .map(answer => {
            return answer.answerOption
          })
          .filter(answerOption => !isEmpty(answerOption))
          .map(answerOption => answerOption?.map(answerOption => answerOption.isSelected).find(isSelected => isSelected))
      })
    })

    // Check if is validation for all question
    if (attrs?.isForAll) {
      const values: any[] = []

      map.forEach(test => {
        values.push(...flattenDeep(test))
      })

      return values.every(result => result)
    }

    return false
  }, [])

  return { question_renderQuestion, question_renderAnswer, question_validate }
}

export { useQuestion }
